import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TOOL_IDS, TOOL_LABELS, type ToolId } from '../lib/access'
import {
  inviteUser,
  isAdminUsingMockData,
  listUsers,
  resendInvite,
  setUserStatus,
  updateUserTools,
  type ManagedUser,
} from '../lib/admin-api'
import MockDataBanner from '../components/finance/MockDataBanner'
import {
  Card,
  Chip,
  LoadingRows,
  inputClass,
  primaryBtn,
  secondaryBtn,
  shortDate,
} from '../components/family-finance/shared'

function InviteForm({ onInvited }: { onInvited: () => void }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [tools, setTools] = useState<ToolId[]>(['family-finance'])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  function toggleTool(tool: ToolId) {
    setTools((prev) => (prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (!email.includes('@') || !name.trim()) {
      setError('A valid email and name are required.')
      return
    }
    if (tools.length === 0) {
      setError('Select at least one tool to grant access to.')
      return
    }
    setSending(true)
    try {
      const user = await inviteUser({ email, name, tools })
      setSuccess(`Invitation sent to ${user.email}. They'll receive an email with a temporary password.`)
      setEmail('')
      setName('')
      setTools(['family-finance'])
      onInvited()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed')
    } finally {
      setSending(false)
    }
  }

  return (
    <Card
      title="Invite a user"
      subtitle="They get their own login and see only the tools you grant — nothing else"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name (e.g. Meagan O'Leary)"
            className={inputClass}
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            type="email"
            className={inputClass}
          />
        </div>
        <div>
          <p className="text-slate-500 dark:text-slate-400 text-xs mb-2">Tool access</p>
          <div className="flex flex-wrap gap-2">
            {TOOL_IDS.map((tool) => (
              <button
                key={tool}
                type="button"
                onClick={() => toggleTool(tool)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  tools.includes(tool)
                    ? 'bg-blue-600/20 border-blue-500 text-blue-700 dark:text-blue-300'
                    : 'bg-slate-100 dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-400'
                }`}
              >
                {tools.includes(tool) ? '✓ ' : ''}
                {TOOL_LABELS[tool]}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}
        {success && <p className="text-emerald-600 dark:text-emerald-400 text-sm">{success}</p>}
        <div className="flex justify-end">
          <button type="submit" disabled={sending} className={primaryBtn}>
            {sending ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </form>
    </Card>
  )
}

function UserRow({ user, onChanged }: { user: ManagedUser; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  async function toggleTool(tool: ToolId) {
    if (user.isAdmin) return
    setBusy(true)
    try {
      const tools = user.tools.includes(tool)
        ? user.tools.filter((t) => t !== tool)
        : [...user.tools, tool]
      await updateUserTools(user.email, tools)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  async function handleResend() {
    setBusy(true)
    try {
      await resendInvite(user.email)
      setMessage('Invite re-sent ✓')
    } finally {
      setBusy(false)
    }
  }

  async function handleToggleDisabled() {
    const next = user.status === 'disabled' ? 'active' : 'disabled'
    if (
      next === 'disabled' &&
      !window.confirm(`Disable ${user.email}? They will no longer be able to sign in.`)
    )
      return
    setBusy(true)
    try {
      await setUserStatus(user.email, next)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="py-4 border-b border-slate-200/50 dark:border-slate-700/50 last:border-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-slate-900 dark:text-white font-medium">
            {user.name}
            {user.isAdmin && <Chip tone="purple">admin</Chip>}
          </p>
          <p className="text-slate-500 text-sm">{user.email}</p>
          <p className="text-slate-400 dark:text-slate-600 text-xs mt-0.5">
            Invited {shortDate(user.invitedAt)}
            {user.lastSignIn ? ` · last sign-in ${shortDate(user.lastSignIn)}` : ' · has not signed in yet'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Chip tone={user.status === 'active' ? 'green' : user.status === 'invited' ? 'amber' : 'red'}>
            {user.status}
          </Chip>
          {user.status === 'invited' && (
            <button onClick={handleResend} disabled={busy} className={secondaryBtn}>
              Resend invite
            </button>
          )}
          {!user.isAdmin && (
            <button
              onClick={handleToggleDisabled}
              disabled={busy}
              className={
                user.status === 'disabled'
                  ? secondaryBtn
                  : 'px-3 py-1.5 text-sm text-red-600/80 dark:text-red-400/80 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors'
              }
            >
              {user.status === 'disabled' ? 'Re-enable' : 'Disable'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {TOOL_IDS.map((tool) => {
          const granted = user.isAdmin || user.tools.includes(tool)
          return (
            <button
              key={tool}
              onClick={() => toggleTool(tool)}
              disabled={busy || user.isAdmin}
              title={user.isAdmin ? 'Admins always have access to every tool' : 'Click to toggle access'}
              className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                granted
                  ? 'bg-emerald-600/10 border-emerald-600/40 text-emerald-600 dark:text-emerald-400'
                  : 'bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-400 dark:hover:border-slate-500'
              } ${user.isAdmin ? 'cursor-default' : ''}`}
            >
              {granted ? '✓' : '✕'} {TOOL_LABELS[tool]}
            </button>
          )
        })}
      </div>
      {message && <p className="text-emerald-600 dark:text-emerald-400 text-xs mt-2">{message}</p>}
    </div>
  )
}

export default function AdminPage() {
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [loading, setLoading] = useState(true)

  const reload = useCallback(async () => {
    setUsers(await listUsers())
    setLoading(false)
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dashboard" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <span className="text-3xl">🛡️</span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Admin</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Invite users and control which tools each person can use</p>
        </div>
      </div>

      <MockDataBanner visible={isAdminUsingMockData()} />

      <div className="space-y-6 max-w-3xl">
        <InviteForm onInvited={reload} />

        <Card title="Users" subtitle="Everyone with a login on nathanaelbrown.info">
          {loading ? (
            <LoadingRows rows={2} />
          ) : (
            users.map((u) => <UserRow key={u.email} user={u} onChanged={reload} />)
          )}
        </Card>

        <Card title="How access control works">
          <ul className="text-slate-500 dark:text-slate-400 text-sm space-y-2 list-disc list-inside">
            <li>
              Each invited user gets their own Cognito login (temporary password by email; they set their own
              on first sign-in).
            </li>
            <li>
              Access is per-tool: someone granted only <span className="text-slate-800 dark:text-slate-200">Finances</span> sees
              just that card and route — every other tool stays hidden and blocked.
            </li>
            <li>Admins (currently you) see everything, including this panel.</li>
            <li>
              CPA access is separate and narrower — it's granted from the Taxes tab and only opens the shared
              document folder, never the app itself.
            </li>
          </ul>
        </Card>
      </div>
    </div>
  )
}
